import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { normalizeInstagramHandle } from "../../utils/normalizeInstagramHandle";
import { t } from "../../i18n";
import { trackEvent } from "../../lib/analytics";

interface SearchHandleFormProps {
  placeholder: string;
  ctaLabel: string;
  helperText?: string;
  autoFocus?: boolean;
  typewriterExamples?: readonly string[];
  initialValue?: string;
}

const DEFAULT_EXAMPLES = [
  "kyliejenner",
  "neymarjr",
  "arianagrande",
  "leomessi",
  "zendaya",
  "taylorswift",
] as const;

function InstagramIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
    >
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </svg>
  );
}

export function SearchHandleForm({
  placeholder,
  ctaLabel,
  autoFocus = false,
  typewriterExamples,
  initialValue = "",
}: Omit<SearchHandleFormProps, "helperText">) {
  const navigate = useNavigate();
  const [value, setValue] = useState(initialValue.replace(/^@/, ""));
  const [error, setError] = useState<string | null>(null);
  const [placeholderText, setPlaceholderText] = useState("");

  useEffect(() => {
    // Strip leading '@' from examples list to avoid duplicates in the animation
    const rawList =
      typewriterExamples && typewriterExamples.length > 0
        ? typewriterExamples
        : DEFAULT_EXAMPLES;

    const list = rawList.map((item) => item.replace(/^@/, ""));
    const cleanPlaceholder = placeholder.replace(/^@/, "").replace(" @", " ");

    const examples = [cleanPlaceholder, ...list];
    let exampleIndex = 0;
    let charIndex = 0;
    let deleting = false;
    let timeoutId: number;

    const tick = () => {
      const current = examples[exampleIndex];
      const nextCharIndex = deleting ? charIndex - 1 : charIndex + 1;
      const nextText = current.slice(0, Math.max(0, nextCharIndex));

      setPlaceholderText(nextText || " ");
      charIndex = nextCharIndex;

      if (!deleting && charIndex >= current.length) {
        deleting = true;
        timeoutId = window.setTimeout(tick, 1200);
        return;
      }

      if (deleting && charIndex <= 0) {
        deleting = false;
        exampleIndex = (exampleIndex + 1) % examples.length;
        timeoutId = window.setTimeout(tick, 300);
        return;
      }

      timeoutId = window.setTimeout(tick, deleting ? 40 : 80);
    };

    timeoutId = window.setTimeout(tick, 500);

    return () => window.clearTimeout(timeoutId);
  }, [placeholder, typewriterExamples]);

  useEffect(() => {
    setValue(initialValue.replace(/^@/, ""));
  }, [initialValue]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const result = normalizeInstagramHandle(value);

    if (!result.ok) {
      setError(result.error);
      return;
    }

    setError(null);
    trackEvent("stalkie_search_submitted", {
      content_path: window.location.pathname,
      acquisition_source:
        new URLSearchParams(window.location.search).get("utm_source") ??
        undefined,
    });
    navigate(`/search?handle=${encodeURIComponent(result.handle)}`);
  }

  return (
    <form className="st-search-form" onSubmit={handleSubmit} noValidate>
      <div className="st-search-bar">
        <div className="st-search-input-row">
          <div className="input-icon">
            <InstagramIcon size={24} />
          </div>
          <span className="st-search-at" aria-hidden="true">
            @
          </span>
          <input
            value={value}
            onChange={(event) => {
              const val = event.target.value;
              // Strip leading '@' if typed or pasted by the user
              const cleanVal = val.startsWith("@") ? val.slice(1) : val;
              setValue(cleanVal);
              setError(null);
            }}
            style={{ paddingLeft: "4px" }}
            placeholder={placeholderText}
            autoFocus={autoFocus}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            aria-label="Instagram username"
          />
        </div>
        <button type="submit">{ctaLabel}</button>
      </div>
      {error ? <p className="st-form-error">{t(error)}</p> : null}
    </form>
  );
}
